import { once } from "@omni/utils/function.utils";
import { cn } from "@omni/utils/styles";
import { ComponentType, useEffect } from "react";

import { useSearchComponentStateSelector } from "@/redux/modules/value/search/search.selectors";
import { AlgoliaSearchHit } from "@/services/algolia/types/search.types";
import * as ListImpressionTracker from "@/services/tracking/trackers/impression/listImpression.tracker";
import { ResultListHit } from "@/utils/search/types/internal/resultListHit";
import { SearchComponentOfType } from "@/utils/search/types/searchComponent";

import { ContentBlock } from "./components/injected/contentBlock.container";
import { SearchHitContextProvider } from "./context/searchHit.context";
import { isHit } from "./helpers/resultListHitType.helpers";

export type ResultListHitComponentProps<THit extends object> = {
    hit: AlgoliaSearchHit<THit>;
};

type ResultListProps<THit extends object> = {
    component: SearchComponentOfType<"RESULT_LIST">;
    hits: Array<ResultListHit<AlgoliaSearchHit<THit>>>;
    className?: string;
    keyExtractor: (hit: THit) => string | number;
    HitComponent: ComponentType<ResultListHitComponentProps<THit>>;
};

const getIndexName = (index: string) => {
    const [, , indexName] = index.split("_");
    return indexName;
};

const ResultList = <THit extends object>({
    component,
    hits,
    className,
    keyExtractor,
    HitComponent
}: ResultListProps<THit>) => {
    const resultListComponentState = useSearchComponentStateSelector(component);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (!hits) return;

        once(() =>
            ListImpressionTracker.track({
                name: getIndexName(resultListComponentState!.index),
                ids: hits.filter(isHit).map(keyExtractor).map(String),
                meta: {
                    index: resultListComponentState?.index
                }
            })
        )();
    }, []);

    if (!hits) return null;

    return (
        <section
            className={cn(
                "tw:grid tw:relative tw:gap-5 tw:grid-cols-1",
                "tw:sm:grid-cols-2",
                "tw:md:grid-cols-3",
                "tw:xl:grid-cols-4",
                className
            )}
        >
            {hits.map((hit, index) =>
                isHit(hit) ? (
                    <SearchHitContextProvider
                        queryID={hit._queryID}
                        position={hit._position}
                        key={hit.objectID}
                    >
                        <HitComponent hit={hit} />
                    </SearchHitContextProvider>
                ) : (
                    <ContentBlock
                        key={`injected_${hit.url}_${index}`}
                        content={hit}
                    />
                )
            )}
        </section>
    );
};

export { ResultList };
